import React from 'react'
import {
  Navigate,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
  useRouteError,
} from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import RequireAuth from '@app/components/router/require-auth'
import generalErrorPage from '@app/assets/img/404errorPage.svg'
import { withLoading } from '@app/hocs/with-loading.hoc'
import { recoverFromChunkError } from '@/hocs/error-boundary'
import { useAppSelector } from '@app/hooks/reduxHooks'
import AdminLayout, { FIRST_ADMIN_TAB } from '@app/layouts/admin/layout'
import DeviceLayout from '@app/layouts/device/layout'
import MainLayout from '@app/layouts/main/layout'
import RodoLayout from '@app/layouts/rodo/layout'
import ServiceLayout from '@/layouts/service/layout'
import ProfileLayout from '@/layouts/profile/layout'
import SeparateMapLayout from '@app/layouts/separateMap/layout'
import LoginPage from '@app/pages/auth/login-page'
import MfaSetupPage from '@app/pages/auth/mfa-setup-page'
import MfaVerifyPage from '@app/pages/auth/mfa-verify-page'
import DashboardPage from '@/pages/dashboard/dashboard-page'
import { SeparateMapGate } from '@/features/separateMap'
import { RequireConsent } from '@/features/consent'
import { GlobalError, GlobalErrorPage } from '@/features/global-error'
import { DesktopOnlyRoute } from '@/features/wizard-page/components/DesktopOnlyRoute'
import { useLanguage } from '@/hooks'

import ProtectedRoute, { getFallbackPath } from './protected-route'

const AuthLayout = React.lazy(() => import('@app/layouts/auth/layout'))
const DevicesPage = React.lazy(() => import('@app/pages/admin/devices'))
const ResetPasswordPage = React.lazy(() => import('@app/pages/auth/reset-password-page'))
const ConsentPage = React.lazy(() => import('@/pages/consent-page'))
const FirmsPage = React.lazy(() => import('@app/pages/admin/firms'))
const UsersPage = React.lazy(() => import('@app/pages/admin/users'))
const InvertersAndAlarmsPage = React.lazy(() => import('@app/pages/admin/inverters-and-alarms'))
const NotificationsPage = React.lazy(() => import('@app/pages/admin/notifications'))
const DevicePage = React.lazy(() => import('@app/pages/admin/device'))
const DeviceAlarmsPage = React.lazy(() => import('@app/pages/device-alarms-page'))
const DeviceHelpPage = React.lazy(() => import('@app/pages/device-help-page'))
const TechnicalInspectionHistoryPage = React.lazy(() => import('@app/pages/technical-inspection-history-page'))
const DeviceServiceRequestsPage = React.lazy(() => import('@/pages/device-service-requests-page'))
const WizardPage = React.lazy(() => import('@app/pages/admin/wizard'))
const WizardEditorPage = React.lazy(() => import('@/pages/admin/wizard-editor'))
const Logout = React.lazy(() => import('./logout'))
const ProfilePage = React.lazy(() => import('@app/pages/profile-page'))
const ProfileSettingsPage = React.lazy(() => import('@/pages/profile-settings-page'))
const ProfileDocumentsPage = React.lazy(() => import('@/pages/profile-documents-page'))
const SeparateMapPage = React.lazy(() => import('@app/pages/separate-map-page'))
const UsersConsentsPage = React.lazy(() => import('@app/pages/rodo/users-consents-page'))
const RodoDocumentsPage = React.lazy(() => import('@app/pages/rodo/rodo-documents-page'))
const AuditLogPage = React.lazy(() => import('@app/pages/audit-log/audit-log-page'))
const ServiceOrdersPage = React.lazy(() => import('@/pages/service/service-orders-page'))
const ServiceRequestsPage = React.lazy(() => import('@/pages/service/service-requests-page'))
const ServiceArchivePage = React.lazy(() => import('@/pages/service/service-archive-page'))
const ServiceActivitiesPage = React.lazy(() => import('@/pages/service/service-activities-page'))
const ServiceOrderExecutionPage = React.lazy(() => import('@/pages/service/service-order-execution-page'))
const MyOrdersPage = React.lazy(() => import('@/pages/my-orders/my-orders-page'))

export const DASHBOARD_PATH = '/'

const Dashboard = withLoading(DashboardPage)
const Devices = withLoading(DevicesPage)
const Firms = withLoading(FirmsPage)
const Users = withLoading(UsersPage)
const Wizard = withLoading(WizardPage)
const WizardEditor = withLoading(WizardEditorPage)
const InvertersAndAlarms = withLoading(InvertersAndAlarmsPage)
const Notifications = withLoading(NotificationsPage)
const Profile = withLoading(ProfilePage)
const ProfileSettings = withLoading(ProfileSettingsPage)
const ProfileDocuments = withLoading(ProfileDocumentsPage)
const UsersConsents = withLoading(UsersConsentsPage)
const RodoDocuments = withLoading(RodoDocumentsPage)
const AuditLog = withLoading(AuditLogPage)
const ServiceOrders = withLoading(ServiceOrdersPage)
const ServiceRequests = withLoading(ServiceRequestsPage)
const ServiceArchive = withLoading(ServiceArchivePage)
const ServiceActivities = withLoading(ServiceActivitiesPage)
const ServiceOrderExecution = withLoading(ServiceOrderExecutionPage)
const MyOrders = withLoading(MyOrdersPage)
const LogoutFallback = withLoading(Logout)
const ResetPassword = withLoading(ResetPasswordPage)

const HomeRedirect = () => {
  const profile = useAppSelector((state) => state.profile?.data)
  const roles: string[] = profile?.role || ['USER']

  const landing = getFallbackPath(roles)

  /* Pulpit renderujemy w miejscu, bez przekierowania: '/' zostaje adresem strony głównej,
     tak jak dotąd. Pozostałe role idą na swoją trasę. */
  if (landing === '/dashboard') return <Dashboard />

  return <Navigate to={landing} replace />
}

/** 404 jest komponentem, a nie gotowym elementem z `t(...)`: drzewo tras powstaje
 * raz, przy imporcie modułu, a i18next nie jest wtedy jeszcze zainicjowany —
 * tłumaczenie rozwiązane w tamtym momencie zostałoby na ekranie jako sam klucz. */
const NotFoundPage = () => {
  const { t } = useTranslation()

  return (
    <GlobalError
      title={t('error.pageNotFound')}
      description={t('error.pageNotFoundDescription')}
      imgData={{ src: generalErrorPage, alt: 'Error page' }}
    />
  )
}

/** Granica błędów wszystkich tras. RouterProvider opakowuje elementy tras własnym
 * boundary i wyjątek renderu NIE dociera już do `<ErrorBoundary>` nad `<App/>` —
 * bez tego elementu użytkownik zobaczyłby domyślny ekran react-routera, a błąd
 * nieświeżego chunku po deployu przestałby przeładowywać stronę. */
const RouteErrorFallback = () => {
  const error = useRouteError()

  React.useEffect(() => {
    if (recoverFromChunkError(error)) return
    console.error('Error caught in route: ', error)
  }, [error])

  /* GlobalError twardo przechodzi na '/' własnym przyciskiem, więc reset stanu
     nie ma tu czego czyścić — trasa i tak montuje się od nowa. */
  return <GlobalErrorPage resetError={() => undefined} />
}

const protectedLayout = (
  <RequireAuth>
    <RequireConsent>
      <MainLayout />
    </RequireConsent>
  </RequireAuth>
)

/* Data router (`createBrowserRouter`), a nie `<BrowserRouter>`: tylko on udostępnia
   `useBlocker`, czyli jedyne wspierane API zatrzymujące nawigację POP (przycisk
   „Wstecz” przeglądarki). Korzysta z niego guard niezapisanych zmian w edytorze
   szablonu. Router powstaje raz na moduł — instancja tworzona przy renderze
   montowałaby całe drzewo od nowa przy każdym przerysowaniu. */
const router = createBrowserRouter(
  createRoutesFromElements(
    <Route errorElement={<RouteErrorFallback />}>
      {/* Bramka feature'u zamiast gołego ProtectedRoute: trasa żyje poza MainLayoutem,
          więc profil na zimnym starcie okna pobiera SeparateMapGate. */}
      <Route element={<SeparateMapGate />}>
        <Route
          path='/separated-map'
          element={
            <RequireAuth>
              <RequireConsent>
                <SeparateMapLayout>
                  <SeparateMapPage />
                </SeparateMapLayout>
              </RequireConsent>
            </RequireAuth>
          }
        />
      </Route>
      <Route path={DASHBOARD_PATH} element={protectedLayout}>
        <Route index element={<HomeRedirect />} />
        <Route element={<ProtectedRoute requiredRoles={['ADMIN', 'USER']} />}>
          <Route path='/dashboard' element={<Dashboard />} />
        </Route>
        <Route element={<ProtectedRoute requiredRoles={['ADMIN', 'USER', 'SERVICE_MANAGER', 'SERVICE_TECHNICIAN']} />}>
          <Route path='/device/:id' element={<DeviceLayout />}>
            <Route index element={<DevicePage />} />
            <Route path='alarms' element={<DeviceAlarmsPage />} />
            <Route path='technical-inspection' element={<TechnicalInspectionHistoryPage />} />
            <Route path='technical-inspection-history' element={<TechnicalInspectionHistoryPage />} />
            <Route path='help' element={<DeviceHelpPage />} />
            <Route path='service-requests' element={<DeviceServiceRequestsPage />}>
              <Route path='new' element={<></>} />
            </Route>
          </Route>
        </Route>
        <Route path='/profile' element={<ProfileLayout />}>
          <Route index element={<Profile />} />
          <Route path='documents' element={<ProfileDocuments />} />
        </Route>
        <Route path='/profile/settings' element={<ProfileSettings />} />
        <Route path='/preferences' element={<Navigate to='/profile/settings?tab=appearance' replace />} />
        <Route path='/settings' element={<Navigate to='/profile/settings' replace />} />
        <Route element={<ProtectedRoute requiredRoles={['ADMIN']} />}>
          <Route path='admin' element={<AdminLayout />}>
            <Route index element={<Navigate to={FIRST_ADMIN_TAB} replace />} />
            <Route path='users' element={<Users />} />
            <Route path='users/new' element={<Users />} />
            <Route path='users/:id' element={<Users />} />
            <Route path='users/:id/edit' element={<Users />} />
            <Route path='firms' element={<Firms />} />
            <Route path='firms/new' element={<Firms />} />
            <Route path='firms/:id' element={<Firms />} />
            <Route path='firms/:id/edit' element={<Firms />} />
            <Route path='devices' element={<Devices />} />
            <Route path='devices/new' element={<Devices />} />
            <Route path='devices/:id' element={<Devices />} />
            <Route path='devices/:id/edit' element={<Devices />} />
            <Route path='inverters-and-alarms' element={<InvertersAndAlarms />} />
            <Route path='inverters-and-alarms/inverters' element={<InvertersAndAlarms />} />
            <Route path='inverters-and-alarms/inverters/new' element={<InvertersAndAlarms />} />
            <Route path='inverters-and-alarms/inverters/:id' element={<InvertersAndAlarms />} />
            <Route path='inverters-and-alarms/inverters/:id/edit' element={<InvertersAndAlarms />} />
            <Route path='inverters-and-alarms/alarms' element={<InvertersAndAlarms />} />
            <Route path='inverters-and-alarms/alarms/new' element={<InvertersAndAlarms />} />
            <Route path='inverters-and-alarms/alarms/:id' element={<InvertersAndAlarms />} />
            <Route path='inverters-and-alarms/alarms/:id/edit' element={<InvertersAndAlarms />} />
            <Route path='notifications' element={<Notifications />} />
            <Route path='notifications/list' element={<Notifications />} />
            <Route path='notifications/list/new' element={<Notifications />} />
            <Route path='notifications/list/:id' element={<Notifications />} />
            <Route path='notifications/list/:id/edit' element={<Notifications />} />
            <Route path='notifications/templates' element={<Notifications />} />
            <Route path='notifications/templates/new' element={<Notifications />} />
            <Route path='notifications/templates/:id' element={<Notifications />} />
            <Route path='notifications/templates/:id/edit' element={<Notifications />} />
            <Route path='notifications/senders' element={<Notifications />} />
            <Route path='notifications/senders/new' element={<Notifications />} />
            <Route path='notifications/senders/:id' element={<Notifications />} />
            <Route path='notifications/senders/:id/edit' element={<Notifications />} />
            <Route path='notifications/recipients' element={<Notifications />} />
            <Route path='notifications/recipients/new' element={<Notifications />} />
            <Route path='notifications/recipients/:id' element={<Notifications />} />
            <Route path='notifications/recipients/:id/edit' element={<Notifications />} />
            <Route path='wizard' element={<Wizard />} />
            <Route element={<DesktopOnlyRoute fallback='/admin/wizard' />}>
              <Route path='wizard/:id/elements' element={<WizardEditor />} />
              <Route path='wizard/:id/edit' element={<WizardEditor />} />
              <Route path='wizard/:id/reset-data' element={<WizardEditor />} />
            </Route>
          </Route>
        </Route>
        <Route element={<ProtectedRoute requiredRoles={['ADMIN_RODO']} />}>
          <Route path='rodo' element={<RodoLayout />}>
            <Route index element={<Navigate to='/rodo/users-consents' replace />} />
            <Route path='users-consents' element={<UsersConsents />} />
            <Route path='privacy-policies' element={<RodoDocuments />} />
            <Route path='data-processing-consents' element={<RodoDocuments />} />
            <Route path='documents' element={<Navigate to='/rodo/privacy-policies' replace />} />
          </Route>
        </Route>
        <Route element={<ProtectedRoute requiredRoles={['ADMIN_AUDIT_LOG']} />}>
          <Route path='audit-log' element={<AuditLog />} />
        </Route>
        <Route element={<ProtectedRoute requiredRoles={['SERVICE_MANAGER']} />}>
          <Route path='service' element={<ServiceLayout />}>
            <Route path='' element={<ServiceOrders />}>
              <Route path='orders/new' element={<></>} />
              <Route path='orders/:id' element={<></>} />
              <Route path='orders/:id/edit' element={<></>} />
            </Route>
            <Route path='orders/:id/execute' element={<ServiceOrderExecution />} />
            <Route path='requests' element={<ServiceRequests />}>
              <Route path=':id' element={<></>} />
            </Route>
            <Route path='archive' element={<ServiceArchive />} />
            <Route path='archive/:id' element={<ServiceOrderExecution />} />
            <Route path='activities' element={<ServiceActivities />}>
              <Route path='new' element={<></>} />
              <Route path=':id' element={<></>} />
              <Route path=':id/edit' element={<></>} />
            </Route>
          </Route>
        </Route>
        <Route element={<ProtectedRoute requiredRoles={['SERVICE_TECHNICIAN']} />}>
          <Route path='my-orders' element={<MyOrders />} />
          <Route path='my-orders/:id' element={<ServiceOrderExecution />} />
          <Route path='my-orders/:relatedTo/related/:id' element={<ServiceOrderExecution />} />
        </Route>
      </Route>
      <Route
        path='/consent'
        element={
          <RequireAuth>
            <AuthLayout />
          </RequireAuth>
        }
      >
        <Route index element={<ConsentPage />} />
      </Route>
      <Route path='/auth' element={<AuthLayout />}>
        <Route path='login' element={<LoginPage />} />
        <Route path='reset-password' element={<ResetPassword />} />
        <Route path='mfa-verify' element={<MfaVerifyPage />} />
        <Route path='mfa-setup' element={<MfaSetupPage />} />
      </Route>
      <Route path='/logout' element={<LogoutFallback />} />
      <Route path='*' element={<NotFoundPage />} />
    </Route>,
  ),
)

export const AppRouter = () => {
  const { language, setLanguage } = useLanguage()

  React.useEffect(() => {
    if (!language) setLanguage('pl')
  }, [language, setLanguage])

  return <RouterProvider router={router} />
}
